/**
 * Map backend history rows to the shape the HistoryTab table renders, and
 * format ISO timestamps into a readable local date/time.
 *
 * Backend row shape (see GET /items/{id}/history):
 *   { id, version, title, description, attributes, author, source,
 *     change_type, changes, timestamp }
 *
 * HistoryTab expects version rows as: { entryType:'version', version, date,
 * author, changes }. A sibling reorder (#520) arrives as an ORDINARY version
 * row with change_type:'reorder' — it's a genuine version-chain mutation, not
 * a separate stream, so no extra mapping is needed here (HistoryTab flags it
 * visually off changeType). Comment events (#389) arrive with entry_type
 * 'comment_event' and map to { entryType:'comment_event', event, commentId,
 * date, author, body/resolution } — no version, so the diff/restore UI skips
 * them. AI-review and approval events (#520) follow the SAME pattern:
 * entry_type 'ai_review_event' -> { entryType, event, date, author, model,
 * scores }; entry_type 'approval_event' -> { entryType, event, date, author,
 * reviewer, stale }.
 *
 * #517: version rows also carry a structured `source` ("UI"/"MCP"/"API"/
 * "SYSTEM") alongside `author`. Before #517, `author` was the ONLY place the
 * surface lived — #390 composed it in as a suffix, "<name> (via UI|MCP)".
 * `actorDisplay` below prefers the structured field: when `author` already
 * carries that composed suffix, it re-derives the "(via SOURCE)" tag from the
 * authoritative `source` column instead of trusting the embedded substring
 * (today they always agree — both come from the same write — but this is
 * forward-compatible with the backend one day dropping the composed suffix
 * from `author` entirely, per #517's "optionally stop composing it" note).
 * An author with NO matching suffix (an explicit `X-Author` override, e.g.
 * "release-bot") is left untouched — composing a source onto an explicitly
 * chosen display name would be a user-visible display change, not just an
 * internal refactor. A row with no `source` at all (every legacy pre-#517
 * row, and #520's review/approval-event authors, which don't carry `source`)
 * falls back to the raw `author` string unchanged — identical to pre-#517
 * behaviour.
 */
const VIA_SOURCE_RE = /^(.*?)\s*\(via\s+[a-zA-Z]+\)\s*$/;

export function actorDisplay(author, source) {
  if (!author || !source) return author;
  const m = VIA_SOURCE_RE.exec(author);
  if (!m) return author;
  return `${m[1]} (via ${source})`;
}

export function formatHistoryDate(timestamp) {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return String(timestamp);
  // Pinned to en-US with an unpadded hour: some runtime locales render a
  // 12-hour clock zero-padded with a `.` separator (e.g. "01.52 PM"), and
  // `hour: '2-digit'` alone just swaps the separator to "01:52 PM" — still a
  // zero-padded 12-hour reading nobody writes by hand. `hour: 'numeric'`
  // gives the conventional "1:52 PM" (en-US never falls back to 24-hour).
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

/**
 * Compact relative time, e.g. "just now", "5m ago", "3h ago", "2d ago".
 * Falls back to an absolute date for anything older than a week.
 */
export function formatRelativeTime(timestamp, now = Date.now()) {
  if (!timestamp) return '';
  const then = new Date(timestamp).getTime();
  if (Number.isNaN(then)) return String(timestamp);
  const secs = Math.round((now - then) / 1000);
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatHistoryDate(timestamp);
}

export function toHistoryRows(history) {
  if (!Array.isArray(history)) return [];
  return history.map((h) => {
    if (h.entry_type === 'comment_event') {
      return {
        entryType: 'comment_event',
        event: h.event || 'comment_added',
        commentId: h.comment_id || null,
        parentCommentId: h.parent_comment_id || null,
        date: formatHistoryDate(h.timestamp),
        author: h.author || 'user',
        body: h.body || '',
        resolution: h.resolution || '',
        status: h.status || '',
      };
    }
    if (h.entry_type === 'ai_review_event') {
      return {
        entryType: 'ai_review_event',
        event: h.event || 'ai_review_completed',
        // Raw ISO timestamp, kept alongside the minute-precision formatted `date`
        // above so HistoryTab can build a React `key` that survives a new event
        // being inserted at the top of the (newest-first) list — #943.
        timestamp: h.timestamp || null,
        date: formatHistoryDate(h.timestamp),
        author: h.author || 'AI reviewer',
        model: h.model || '',
        scores: h.scores || null,
      };
    }
    if (h.entry_type === 'approval_event') {
      return {
        entryType: 'approval_event',
        // NOT `|| 'approved'`. Defaulting an unlabelled governance transition to
        // the most favourable value it could possibly have is the one direction a
        // guess must never go in an audit feed — and it fed HistoryTab's own
        // fallback, which had already mislabelled `cleared` and `overridden` as
        // "Approved" for exactly this reason.
        event: h.event || 'unknown',
        // See the ai_review_event branch above — same key-stability reason.
        timestamp: h.timestamp || null,
        date: formatHistoryDate(h.timestamp),
        author: h.author || h.reviewer || 'user',
        reviewer: h.reviewer || '',
        stale: !!h.stale,
        // The state this transition moved AWAY from — lets a `cleared` entry read
        // "withdrew their approval" rather than a bare "cleared" (#590).
        prevState: h.prev_state || null,
        // INT-4: every field below is returned by `_approval_history_events`
        // (src/web/backend/api.py) and was being DROPPED here, so the History feed —
        // the surface an auditor actually reads — showed only who, when and what state.
        // F-06 / REQ-6096 (#603, D-09): the mandatory rejection rationale.
        reason: h.reason || '',
        // F-08 / REQ-6098 (#603, D-08): why an objection was overruled.
        justification: h.justification || '',
        // F-09 / REQ-6099: WHO was overruled. Already a parsed array on this route
        // (api.py json.loads()es it), unlike the raw string `/breakdown` returns.
        supersededReviewers: Array.isArray(h.superseded_reviewers)
          ? h.superseded_reviewers : [],
        // F-12 / REQ-6102 (#592, D-05): the revision the decision was issued against,
        // and whether the item has since moved on while the decision still reads fresh.
        approvedAtVersion: h.approved_at_version ?? null,
        versionDrift: !!h.version_drift,
        // F-52 / REQ-6113 (#591, D-04): a self-issued sign-off.
        selfApproved: !!h.self_approved,
        // F-60 / REQ-6118 (#606, #629): on an `overridden` event, the privileged
        // actor's id who cleared THIS reviewer's decision. `author` above already
        // carries their display name (backend sets author = actor for this event),
        // but the raw id is kept too — the same "don't drop a field the backend
        // added" lesson INT-4 exists to enforce.
        overrideBy: h.override_by || null,
      };
    }
    return {
      entryType: 'version',
      version: h.version,
      date: formatHistoryDate(h.timestamp),
      author: actorDisplay(h.author, h.source) || 'user',
      source: h.source || null,
      changeType: h.change_type || 'update',
      changes: h.changes || (h.change_type === 'create' ? 'Item created' : ''),
      reason: h.reason || '',
    };
  });
}
